/* ── charts & meters ────────────────────────────────────────── */
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { FeedIcon } from '../Icons3D';
import { Num, countMetric } from './numbers';

export function Bar({ pct: p, color = 'var(--brand)', height = 6 }) {
  return (
    <div className="bar" style={{ height }}>
      <i style={{ width: `${Math.min(100, Math.max(0, p))}%`, background: color }} />
    </div>
  );
}

/**
 * label · bar · value row — drivers, risk buckets, factor strength.
 *
 * `valuesLeft` puts the figure between the name and the bar, as `FactorBar`
 * does: the number is what the reader takes away and the bar is the picture of
 * it, so a column of figures beside the names beats one pushed to the far edge
 * past bars of every length. The caller gives `cols` in the order it wants —
 * name, value, bar — because only it knows how wide its own figures run.
 */
export function FBarRow({
  label, pct: p, value, color = 'var(--brand)', valueTone, valuesLeft,
  cols = '126px 1fr 56px', onClick, metricKey, entity, holding, scenario, type,
}) {
  const bar = (
    <div className="bar" style={{ height: 11 }}>
      <i style={{ width: `${Math.min(100, Math.max(0, p))}%`, background: color }} />
    </div>
  );
  const figure = (
    <span className={`val ${valueTone || ''}`}>
      <Num
        value={value}
        label={label}
        metricKey={metricKey ?? countMetric(label)}
        entity={entity}
        holding={holding}
        scenario={scenario}
        type={type}
      />
    </span>
  );

  return (
    <div
      className={valuesLeft ? 'fbar-row vleft' : 'fbar-row'}
      style={{ gridTemplateColumns: cols }}
      onClick={onClick}
    >
      <span className="lbl">{label}</span>
      {valuesLeft ? <>{figure}{bar}</> : <>{bar}{figure}</>}
    </div>
  );
}

/**
 * Centred ±bar used for factor exposure vs benchmark.
 *
 * `onClick` makes the row open something — the holdings behind the factor —
 * and is what puts the caret there, so a bar with nothing to open does not
 * advertise that it has. The value keeps its own click: `Num` stops the event
 * so opening a series does not also toggle the row underneath it.
 */
export function FactorBar({
  label, value, benchmark, display, aside, valuesLeft, metricKey, entity, onClick, open,
}) {
  const half = Math.min(50, Math.abs(value) * 50);
  /* `dual` opens a second value column — the fund's loading and the
     benchmark's. `vleft` puts the figures before the track, so a reader takes
     the numbers first and the bar as the picture of them. */
  const cls = ['fbar-row factor', aside === undefined ? '' : 'dual', valuesLeft ? 'vleft' : '']
    .filter(Boolean).join(' ');

  const values = (
    <>
      <span className="val">
        {/* A factor loading is a score, not a rate — it was quoted as a
            percentage here and as currency in the series it opened. */}
        {metricKey
          ? <Num value={display} label={label} metricKey={metricKey} entity={entity} type="num" />
          : display}
      </span>
      {aside !== undefined && <span className="val aside">{aside}</span>}
    </>
  );

  const track = (
    <div className="fbar-track">
      <div
        className="fbar-fill"
        style={{
          left: value >= 0 ? '50%' : `${50 - half}%`,
          width: `${half}%`,
          background: value >= 0 ? 'var(--brand)' : 'var(--ink2)',
        }}
      />
      {benchmark != null && (
        <div className="fbar-mk" style={{ left: `${50 + Math.max(-49, Math.min(49, benchmark * 50))}%` }} />
      )}
    </div>
  );

  return (
    <div className={cls} onClick={onClick} style={onClick ? { cursor: 'pointer' } : undefined}>
      <span className="lbl">{onClick ? (open ? '▾ ' : '▸ ') : ''}{label}</span>
      {valuesLeft ? <>{values}{track}</> : <>{track}{values}</>}
    </div>
  );
}

/** Attribution row: negative bar · label · positive bar. */
export function AttrRow({ label, value, max, onClick }) {
  const w = max ? Math.min(100, (Math.abs(value) / max) * 100) : 0;
  return (
    <div className="attr-row" onClick={onClick}>
      <div className="attr-neg">{value < 0 && <i style={{ width: `${w}%` }} />}</div>
      <div className="attr-mid">{label}</div>
      <div className="attr-pos">{value >= 0 && <i style={{ width: `${w}%` }} />}</div>
    </div>
  );
}

const DONUT_COLORS = [
  'var(--brand)',
  'color-mix(in srgb,var(--brand) 55%,var(--panel))',
  'color-mix(in srgb,var(--brand) 30%,var(--panel))',
  'var(--ink2)',
  'var(--green)',
  'color-mix(in srgb,var(--ink2) 45%,var(--panel))',
];

export function donutColor(i) {
  return DONUT_COLORS[i % DONUT_COLORS.length];
}

/** Segmented donut + legend — AUM mix, asset allocation, book split. */
export function Donut({ slices, size = 110 }) {
  const total = slices.reduce((s, x) => s + (x.value || 0), 0) || 1;
  let offset = 25;
  return (
    <svg width={size} height={size} viewBox="0 0 42 42">
      <circle cx="21" cy="21" r="15.9" fill="none" stroke="var(--line2)" strokeWidth="6" />
      {slices.map((s, i) => {
        const share = ((s.value || 0) / total) * 100;
        const el = (
          <circle
            key={s.label}
            cx="21"
            cy="21"
            r="15.9"
            fill="none"
            stroke={s.color || donutColor(i)}
            strokeWidth="6"
            strokeDasharray={`${share} ${100 - share}`}
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        );
        offset -= share;
        return el;
      })}
    </svg>
  );
}

export function Legend({ items, onPick }) {
  return (
    <ul className="legend" style={{ flex: 1 }}>
      {items.map((it, i) => (
        <li key={it.label} onClick={onPick ? () => onPick(it) : undefined}>
          <span className="sw" style={{ background: it.color || donutColor(i) }} />
          {it.label}
          <span className="v">{it.display}</span>
        </li>
      ))}
    </ul>
  );
}

/** Big scored ring — portfolio health, fit verdict. */
export function ScoreRing({ score, max = 100, label, size = 132, gradientId = 'gr' }) {
  const share = Math.max(0, Math.min(100, (score / max) * 100));
  return (
    <div className="score-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox="0 0 42 42">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--brand)" />
            <stop offset="100%" stopColor="var(--brand)" />
          </linearGradient>
        </defs>
        <circle cx="21" cy="21" r="15.9" fill="none" stroke="var(--line2)" strokeWidth="4.5" />
        <circle
          cx="21" cy="21" r="15.9" fill="none"
          stroke={`url(#${gradientId})`} strokeWidth="4.5"
          strokeDasharray={`${share} ${100 - share}`} strokeDashoffset="25" strokeLinecap="round"
        />
      </svg>
      <div className="sc">
        <b>{score == null ? '—' : Math.round(score)}</b>
        <span>{label}</span>
      </div>
    </div>
  );
}

/** Person cell used in every client / holder table. */
export function Person({ initials: ini, name, sub }) {
  return (
    <div className="person">
      <div className="p-ava">{ini}</div>
      <div>
        <b>{name}</b>
        <span>{sub}</span>
      </div>
    </div>
  );
}

/** Flag row — analyzer findings, compliance breaches. */
export function Flag({ icon, title, body }) {
  return (
    <div className="flag">
      <FeedIcon name={icon} className="fi ic3fi" />
      <div>
        <b>{title}</b>
        <p>{body}</p>
      </div>
    </div>
  );
}

export function Pipeline({ steps, current }) {
  return (
    <div className="pipeline">
      {steps.map((s, i) => (
        <div key={s} className={`pstep ${i < current ? 'done' : i === current ? 'now' : ''}`}>
          <div className="pd">{i < current ? '✓' : i + 1}</div>
          <span>{s}</span>
        </div>
      ))}
    </div>
  );
}

/* ── time series ────────────────────────────────────────────── */

/** Nice-ish round step for the value axis, so labels land on readable numbers. */
function axisStep(span, target = 4) {
  const raw = span / target;
  const mag = 10 ** Math.floor(Math.log10(raw || 1));
  return [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? mag * 10;
}

/**
 * Several lines on one pair of axes.
 *
 * `TimeSeries` below plots one series and owns its own y-range, which is right
 * for a NAV curve and wrong for a comparison: three separately scaled charts
 * cannot show that the model beat its category, because the whole point is the
 * gap between the lines. This shares one range across every series so the gap
 * is the thing you see, and hovering reports all of them at that x.
 *
 * A null value is a HOLE, not a zero — an index that no investable benchmark
 * covered has to break the line rather than dive to the floor — so each series
 * is drawn as one path per run of consecutive numbers.
 *
 * `labels` are plotted at uniform spacing rather than on a date scale: these are
 * rebalance dates and entry lags, which are evenly spaced by construction.
 */
export function SeriesChart({
  labels, series, height = 260, format = (v) => v, baseline, baselineLabel, marker,
  /* Points ON the line: `{ index, value, level, label }` each. What a review's
     price path uses to put the desk's own buys and sells on a stock's price —
     the chart is the trade, not the price. */
  markers = [],
}) {
  const box = useRef(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState(null);
  /**
   * ZOOM — drag across the plot to read a stretch of it closely.
   *
   * Held as indices into the ORIGINAL data, and applied by slicing before any
   * geometry is worked out, so every scale, tick, path and tooltip below is the
   * unchanged code operating on a shorter series. The y-axis rescales with it,
   * which is the point: a month inside a five-year line is a flat smudge until
   * the axis belongs to the month.
   */
  const [zoom, setZoom] = useState(null);
  /* The drag in progress, as two ratios across the plot. */
  const [drag, setDrag] = useState(null);

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const measure = () => setWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* A zoom that outlives the data it was taken on would slice the wrong days. */
  const pointCount = labels?.length ?? 0;
  useEffect(() => { setZoom(null); setDrag(null); }, [pointCount]);

  const PAD = { top: 10, right: 10, bottom: 22, left: 52 };
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = Math.max(0, height - PAD.top - PAD.bottom);

  /* What is actually drawn: the window, or everything. */
  const base = zoom ? zoom.from : 0;
  const viewLabels = zoom ? labels.slice(zoom.from, zoom.to + 1) : labels;
  const viewSeries = zoom
    ? (series ?? []).map((s) => ({ ...s, values: (s.values ?? []).slice(zoom.from, zoom.to + 1) }))
    : (series ?? []);

  const ratioAt = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return Math.min(1, Math.max(0, (e.clientX - rect.left - PAD.left) / plotW));
  };

  const onMove = (e) => {
    if (!viewLabels?.length || plotW <= 0) return;
    const ratio = ratioAt(e);
    setHover(Math.round(ratio * (viewLabels.length - 1)));
    if (drag) setDrag((d) => (d ? { ...d, b: ratio } : d));
  };

  const onDown = (e) => {
    if (!viewLabels?.length || plotW <= 0) return;
    const ratio = ratioAt(e);
    setDrag({ a: ratio, b: ratio });
  };

  /* At least three points, or the "zoom" is a click that empties the chart. */
  const MIN_POINTS = 3;
  const endDrag = () => {
    if (!drag) return;
    const n = viewLabels.length - 1;
    const i0 = Math.round(Math.min(drag.a, drag.b) * n);
    const i1 = Math.round(Math.max(drag.a, drag.b) * n);
    setDrag(null);
    if (i1 - i0 + 1 < MIN_POINTS) return;
    setZoom({ from: base + i0, to: base + i1 });
    setHover(null);
  };

  const live = (viewSeries ?? []).filter((s) => s.values?.some((v) => v != null));
  if (!viewLabels || viewLabels.length < 2 || !live.length) return null;
  const labelsIn = viewLabels;

  const all = live.flatMap((s) => s.values).filter((v) => v != null).map(Number);
  const withBase = baseline == null ? all : [...all, baseline];
  const step = axisStep(Math.max(...withBase) - Math.min(...withBase) || 1);
  const lo = Math.floor(Math.min(...withBase) / step) * step;
  const hi = Math.ceil(Math.max(...withBase) / step) * step;
  const span = hi - lo || 1;

  const xOf = (i) => PAD.left + (i / (labelsIn.length - 1)) * plotW;
  const yOf = (v) => PAD.top + plotH - ((Number(v) - lo) / span) * plotH;

  /* One path per unbroken run, so a gap in the data is a gap in the line. */
  const paths = (values) => {
    const out = [];
    let run = [];
    values.forEach((v, i) => {
      if (v == null) { if (run.length > 1) out.push(run); run = []; return; }
      run.push(`${run.length ? 'L' : 'M'}${xOf(i).toFixed(1)},${yOf(v).toFixed(1)}`);
    });
    if (run.length > 1) out.push(run);
    return out.map((r) => r.join(' '));
  };

  const ticks = [];
  for (let v = lo; v <= hi + 1e-9; v += step) ticks.push(v);
  const dateTicks = [0, Math.floor((labelsIn.length - 1) / 2), labelsIn.length - 1];

  return (
    <div ref={box} style={{ position: 'relative', width: '100%' }}>
      {width > 0 && (
        <svg
          width={width} height={height}
          onMouseMove={onMove}
          onMouseDown={onDown}
          onMouseUp={endDrag}
          /* Leaving mid-drag commits what was covered rather than throwing the
             selection away — the pointer often exits past the last point. */
          onMouseLeave={() => { endDrag(); setHover(null); }}
          style={{ display: 'block', cursor: drag ? 'ew-resize' : 'crosshair', userSelect: 'none' }}
        >
          {ticks.map((v) => (
            <g key={v}>
              <line x1={PAD.left} x2={width - PAD.right} y1={yOf(v)} y2={yOf(v)} stroke="var(--line2)" />
              <text x={PAD.left - 7} y={yOf(v) + 3} textAnchor="end" fontSize="10" fill="var(--ink3)">
                {format(v)}
              </text>
            </g>
          ))}

          {baseline != null && (
            <g>
              <line
                x1={PAD.left} x2={width - PAD.right} y1={yOf(baseline)} y2={yOf(baseline)}
                stroke="var(--ink3)" strokeWidth="1" strokeDasharray="4 4"
              />
              {baselineLabel && (
                <text x={width - PAD.right} y={yOf(baseline) - 4} textAnchor="end" fontSize="9.5" fill="var(--ink3)">
                  {baselineLabel}
                </text>
              )}
            </g>
          )}

          {(markers ?? []).map((m, i) => {
            /* Markers are given in original indices; the plot is a window. */
            const at = Number(m?.index) - base;
            if (!Number.isFinite(at) || at < 0 || at >= labelsIn.length || m?.value == null) return null;
            const colour = m.level === 'good' ? 'var(--green)' : m.level === 'bad' ? 'var(--red)' : 'var(--brand)';
            return (
              <g key={`${at}-${i}`}>
                <circle cx={xOf(at)} cy={yOf(m.value)} r={4.5} fill={colour} stroke="var(--panel)" strokeWidth="1.5">
                  <title>{[labelsIn[at], m.label, format(m.value)].filter(Boolean).join(' · ')}</title>
                </circle>
              </g>
            );
          })}
          {marker != null && marker >= 0 && marker < labelsIn.length && (
            <line
              x1={xOf(marker)} x2={xOf(marker)} y1={PAD.top} y2={PAD.top + plotH}
              stroke="var(--brand)" strokeWidth="1" strokeDasharray="4 4"
            />
          )}

          {dateTicks.map((i, n) => (
            <text
              key={i} x={xOf(i)} y={height - 6}
              textAnchor={n === 0 ? 'start' : n === dateTicks.length - 1 ? 'end' : 'middle'}
              fontSize="10" fill="var(--ink3)"
            >
              {labelsIn[i]}
            </text>
          ))}

          {live.map((s) => paths(s.values).map((d, n) => (
            <path
              key={`${s.name}-${n}`} d={d} fill="none"
              stroke={s.color || 'var(--brand)'} strokeWidth={s.width || 1.8}
              strokeDasharray={s.dash || undefined} strokeLinejoin="round"
            />
          )))}

          {/* The stretch being dragged over, drawn under the crosshair. */}
          {drag && Math.abs(drag.b - drag.a) > 0.005 && (
            <rect
              x={PAD.left + Math.min(drag.a, drag.b) * plotW}
              y={PAD.top}
              width={Math.abs(drag.b - drag.a) * plotW}
              height={plotH}
              fill="color-mix(in srgb,var(--brand) 14%,transparent)"
              stroke="var(--brand)"
              strokeWidth="1"
            />
          )}

          {/* The stretch being dragged over. */}
          {drag && Math.abs(drag.b - drag.a) > 0.005 && (
            <rect
              x={PAD.left + Math.min(drag.a, drag.b) * plotW}
              y={PAD.top}
              width={Math.abs(drag.b - drag.a) * plotW}
              height={plotH}
              fill="color-mix(in srgb,var(--brand) 14%,transparent)"
              stroke="var(--brand)"
              strokeWidth="1"
            />
          )}

          {hover != null && (
            <>
              <line
                x1={xOf(hover)} x2={xOf(hover)} y1={PAD.top} y2={PAD.top + plotH}
                stroke="var(--ink3)" strokeWidth="1" strokeDasharray="3 3"
              />
              {live.map((s) => (s.values[hover] == null ? null : (
                <circle
                  key={s.name} cx={xOf(hover)} cy={yOf(s.values[hover])} r="3.5"
                  fill="var(--panel)" stroke={s.color || 'var(--brand)'} strokeWidth="2"
                />
              )))}
            </>
          )}
        </svg>
      )}

      {zoom && (
        <button
          type="button"
          onClick={() => { setZoom(null); setHover(null); }}
          style={{
            position: 'absolute', top: 2, right: 2, zIndex: 2,
            border: '1px solid var(--line)', background: 'var(--panel)', color: 'var(--ink2)',
            borderRadius: 7, padding: '2px 8px', fontSize: 10.8, fontWeight: 600, cursor: 'pointer',
          }}
        >
          ⤢ Reset zoom · {labelsIn[0]} → {labelsIn[labelsIn.length - 1]}
        </button>
      )}

      {hover != null && (
        <div
          className="series-tip"
          style={{
            left: xOf(hover) + (hover > labelsIn.length / 2 ? -12 : 12),
            transform: hover > labelsIn.length / 2 ? 'translateX(-100%)' : 'none',
          }}
        >
          <div style={{ color: 'var(--ink3)' }}>{labelsIn[hover]}</div>
          {live.map((s) => (
            <div key={s.name} style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
              <span className="sw" style={{ background: s.color || 'var(--brand)' }} />
              <span style={{ flex: 1 }}>{s.name}</span>
              <b>{s.values[hover] == null ? 'not measured' : format(s.values[hover])}</b>
            </div>
          ))}
        </div>
      )}

      <ul className="legend series-legend">
        {live.map((s) => (
          <li key={s.name}>
            <span className="sw" style={{ background: s.color || 'var(--brand)' }} />
            {s.name}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A line chart you can read a value off.
 *
 * The kit's `Sparkline` is a bare `<polyline>` — no axes, no points, no mouse
 * handlers — which is right for the 86×34 KPI thumbnails it was built for and
 * useless at 420×140, where a reader expects to hover a day and be told which
 * day it was. This is the full-size counterpart: axes, a crosshair that snaps
 * to the nearest point, and a tooltip carrying the date and the value.
 *
 * It measures its container rather than taking a fixed width, because the old
 * chart was pinned at 420px and left a third of its card empty. Rendering at
 * real pixel width instead of scaling a viewBox keeps strokes and label text at
 * their true size at every card width.
 *
 * Points are `{ date, value }`. Hit-testing is by x-position over a uniform
 * index — NAV series are dense and near-daily, so treating them as evenly
 * spaced tracks the cursor closely while avoiding a date-scale for a chart that
 * is read by eye.
 */
/**
 * How long a reading of the overlay is carried forward onto the main series'
 * later dates before it is treated as missing. Longer than any exchange
 * holiday, so a Diwali week does not break the line; shorter than a month, so
 * a position the fund exited — which simply stops appearing in the weight
 * rows — does not ride along at its last weight for ever.
 */
const OVERLAY_STALE_DAYS = 14;

/**
 * The overlay's value on each date of the main series: the last reading at or
 * before that date, or null where there is none or it has gone stale. The two
 * series are both daily but not on the same days — a price is published on
 * trading days and a weight on whichever days the pass wrote — so this is what
 * lets one x-axis carry both.
 *
 * A reading marked `hold` is carried forward until the next reading however
 * old it gets: it is a filed figure that stands until the next filing, the way
 * a disclosed weight rides along unchanged between two monthly disclosures,
 * not a daily measurement whose absence means the pass stopped writing.
 */
function alignOverlay(points, overlayPoints) {
  const sorted = [...overlayPoints].sort((a, b) => (a.date < b.date ? -1 : 1));
  let j = -1;
  return points.map((p) => {
    const d = String(p.date).slice(0, 10);
    while (j + 1 < sorted.length && String(sorted[j + 1].date).slice(0, 10) <= d) j += 1;
    if (j < 0) return null;
    const age = (Date.parse(d) - Date.parse(String(sorted[j].date).slice(0, 10))) / 86400000;
    const v = Number(sorted[j].value);
    if (!Number.isFinite(v)) return null;
    return !sorted[j].hold && age > OVERLAY_STALE_DAYS ? null : v;
  });
}

/** One SVG path per unbroken run of values, so a hole in the data is a gap in the line. */
function brokenPaths(values, xOf, yOf) {
  const out = [];
  let run = [];
  values.forEach((v, i) => {
    if (v == null) { if (run.length > 1) out.push(run); run = []; return; }
    run.push(`${run.length ? 'L' : 'M'}${xOf(i).toFixed(1)},${yOf(v).toFixed(1)}`);
  });
  if (run.length > 1) out.push(run);
  return out.map((r) => r.join(' '));
}

export function TimeSeries({
  points,
  height = 240,
  stroke = 'var(--brand)',
  format = (v) => v,
  label = 'Value',
  /* Events ON the series, each `{ date, level, label }`. Matched to the nearest
     point at or before its date, because a trade is disclosed on a filing date
     the price series need not carry. */
  markers = [],
  /* A second series on its own right-hand axis: `{ points, label, format,
     stroke }`. What puts a fund's weight in a stock over the stock's price, so
     the trades marked on the price can be read against the position they
     built or ran down. Its points need not share the main series' dates —
     see alignOverlay. */
  overlay = null,
}) {
  const box = useRef(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState(null);
  /* Drag across the plot to read a stretch closely; see the note on the slice
     below for why this is held as indices rather than dates. */
  const [zoom, setZoom] = useState(null);
  const [drag, setDrag] = useState(null);
  const gradient = useId();

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const measure = () => setWidth(el.clientWidth);
    measure();
    /* Cards resize with the window and with the sidebar collapsing, and neither
       fires anything this component would otherwise hear about. */
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* A pointer that leaves through the tooltip, or while the tab is switched,
     never fires `mouseleave` on the svg — clear on scroll as well. */
  useEffect(() => {
    if (hover == null) return undefined;
    const clear = () => setHover(null);
    window.addEventListener('scroll', clear, { passive: true });
    return () => window.removeEventListener('scroll', clear);
  }, [hover]);

  /* A zoom taken on one series must not survive into the next. */
  useEffect(() => { setZoom(null); setDrag(null); }, [points.length]);

  /* Room on the right for the overlay's own axis labels, only when it is drawn. */
  const PAD = { top: 10, right: overlay ? 52 : 8, bottom: 22, left: 46 };
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = Math.max(0, height - PAD.top - PAD.bottom);

  const base = zoom ? zoom.from : 0;
  const shown = zoom ? points.slice(zoom.from, zoom.to + 1) : points;

  const ratioAt = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return Math.min(1, Math.max(0, (e.clientX - rect.left - PAD.left) / plotW));
  };
  const onMove = (e) => {
    if (!shown.length || plotW <= 0) return;
    const ratio = ratioAt(e);
    setHover(Math.round(ratio * (shown.length - 1)));
    if (drag) setDrag((d) => (d ? { ...d, b: ratio } : d));
  };
  const onDown = (e) => {
    if (!shown.length || plotW <= 0) return;
    setDrag({ a: ratioAt(e), b: ratioAt(e) });
  };
  /* Fewer than three points is a click, not a window. */
  const endDrag = () => {
    if (!drag) return;
    const n = shown.length - 1;
    const i0 = Math.round(Math.min(drag.a, drag.b) * n);
    const i1 = Math.round(Math.max(drag.a, drag.b) * n);
    setDrag(null);
    if (i1 - i0 + 1 < 3) return;
    setZoom({ from: base + i0, to: base + i1 });
    setHover(null);
  };

  /* The window being read, as indices into the points given. Sliced before any
     geometry, so the scales, ticks, path and tooltip below are the unchanged
     code working on a shorter series — and the y-axis rescales to the window,
     which is what makes a flat-looking stretch legible. */
  const pts = zoom ? points.slice(zoom.from, zoom.to + 1) : points;
  if (!pts || pts.length < 2) return null;

  /* The overlay on the main series' dates, with its own scale: a weight of 5%
     and a price of ₹1,400 share an x-axis and nothing else. Two readings is
     the least that makes a line; below that it is left off rather than drawn
     as a dot nobody can read. */
  const over = overlay?.pts?.length ? alignOverlay(pts, overlay.pts) : null;
  const overValues = over ? over.filter((v) => v != null) : [];
  const hasOver = overValues.length >= 2;
  const step2 = hasOver ? axisStep(Math.max(...overValues) - Math.min(...overValues) || 1) : 1;
  const lo2 = hasOver ? Math.floor(Math.min(...overValues) / step2) * step2 : 0;
  const hi2 = hasOver ? Math.ceil(Math.max(...overValues) / step2) * step2 : 1;
  const span2 = hi2 - lo2 || 1;
  const overStroke = overlay?.stroke || 'var(--amber)';
  const overFormat = overlay?.format || ((v) => v);
  const ticks2 = [];
  if (hasOver) for (let v = lo2; v <= hi2 + 1e-9; v += step2) ticks2.push(v);

  /* Each marker's index in the series: the last point at or before its date. */
  const markerPoints = (markers ?? []).map((m) => {
    const at = String(m?.date || '').slice(0, 10);
    if (!at) return null;
    let idx = -1;
    pts.forEach((p, i) => { if (String(p.date).slice(0, 10) <= at) idx = i; });
    if (idx < 0) return null;
    return { ...m, index: idx, value: Number(pts[idx].value) };
  }).filter(Boolean);

  const values = pts.map((p) => Number(p.value));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const step = axisStep(max - min || 1);
  /* Snap the axis outward to the step so gridlines sit on round numbers. */
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const span = hi - lo || 1;

  const xOf = (i) => PAD.left + (i / (pts.length - 1)) * plotW;
  const yOf = (v) => PAD.top + plotH - ((Number(v) - lo) / span) * plotH;
  const yOf2 = (v) => PAD.top + plotH - ((Number(v) - lo2) / span2) * plotH;

  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${xOf(i).toFixed(1)},${yOf(p.value).toFixed(1)}`).join(' ');
  const area = `${line} L${xOf(pts.length - 1).toFixed(1)},${PAD.top + plotH} L${xOf(0).toFixed(1)},${PAD.top + plotH} Z`;
  const overPaths = hasOver ? brokenPaths(over, xOf, yOf2) : [];

  const ticks = [];
  for (let v = lo; v <= hi + 1e-9; v += step) ticks.push(v);

  /* Three dates across the axis: ends plus the middle, which is as much as fits
     without the labels colliding at a narrow card width. */
  const dateTicks = [0, Math.floor((pts.length - 1) / 2), pts.length - 1];
  const active = hover == null ? null : pts[hover];

  return (
    <div ref={box} style={{ position: 'relative', width: '100%' }}>
      {width > 0 && (
        <svg
          width={width}
          height={height}
          onMouseMove={onMove}
          onMouseDown={onDown}
          onMouseUp={endDrag}
          onMouseLeave={() => { endDrag(); setHover(null); }}
          style={{ display: 'block', cursor: 'crosshair', touchAction: 'none' }}
        >
          <defs>
            <linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity="0.18" />
              <stop offset="100%" stopColor={stroke} stopOpacity="0" />
            </linearGradient>
          </defs>

          {ticks.map((v) => (
            <g key={v}>
              <line
                x1={PAD.left} x2={width - PAD.right} y1={yOf(v)} y2={yOf(v)}
                stroke="var(--line2)" strokeWidth="1"
              />
              <text
                x={PAD.left - 7} y={yOf(v) + 3} textAnchor="end"
                fontSize="10" fill="var(--ink3)"
              >
                {format(v)}
              </text>
            </g>
          ))}

          {/* The overlay's scale, on the right, in its own colour so a reader
              knows which line each column of figures belongs to. */}
          {ticks2.map((v) => (
            <text
              key={`r${v}`} x={width - PAD.right + 7} y={yOf2(v) + 3} textAnchor="start"
              fontSize="10" fill={overStroke}
            >
              {overFormat(v)}
            </text>
          ))}

          {dateTicks.map((i, n) => (
            <text
              key={i}
              x={xOf(i)}
              y={height - 6}
              textAnchor={n === 0 ? 'start' : n === dateTicks.length - 1 ? 'end' : 'middle'}
              fontSize="10"
              fill="var(--ink3)"
            >
              {pts[i].date}
            </text>
          ))}

          <path d={area} fill={`url(#${gradient})`} />
          <path d={line} fill="none" stroke={stroke} strokeWidth="1.8" strokeLinejoin="round" />
          {overPaths.map((d, n) => (
            <path key={`o${n}`} d={d} fill="none" stroke={overStroke} strokeWidth="1.5" strokeLinejoin="round" />
          ))}

          {/* Events on the series — a fund's own adds and trims on the weight it
              holds. Drawn under the crosshair so hovering still reads the line. */}
          {markerPoints.map((m, i) => (
            <circle
              key={`${m.index}-${i}`}
              cx={xOf(m.index)}
              cy={yOf(m.value)}
              r="4.5"
              fill={m.level === 'bad' ? 'var(--red)' : 'var(--green)'}
              stroke="var(--panel)"
              strokeWidth="1.5"
            >
              <title>{[pts[m.index].date, m.label, format(m.value)].filter(Boolean).join(' · ')}</title>
            </circle>
          ))}

          {active && (
            <g>
              <line
                x1={xOf(hover)} x2={xOf(hover)} y1={PAD.top} y2={PAD.top + plotH}
                stroke="var(--ink3)" strokeWidth="1" strokeDasharray="3 3"
              />
              <circle
                cx={xOf(hover)} cy={yOf(active.value)} r="4"
                fill="var(--panel)" stroke={stroke} strokeWidth="2"
              />
              {hasOver && over[hover] != null && (
                <circle
                  cx={xOf(hover)} cy={yOf2(over[hover])} r="4"
                  fill="var(--panel)" stroke={overStroke} strokeWidth="2"
                />
              )}
            </g>
          )}
        </svg>
      )}

      {zoom && (
        <button
          type="button"
          onClick={() => { setZoom(null); setHover(null); }}
          style={{
            position: 'absolute', top: 2, right: 2, zIndex: 2,
            border: '1px solid var(--line)', background: 'var(--panel)', color: 'var(--ink2)',
            borderRadius: 7, padding: '2px 8px', fontSize: 10.8, fontWeight: 600, cursor: 'pointer',
          }}
        >
          ⤢ Reset zoom · {pts[0]?.date} → {pts[pts.length - 1]?.date}
        </button>
      )}

      {active && (
        <div
          style={{
            position: 'absolute',
            /* Flip to the left of the crosshair past halfway, so the tooltip
               never hangs off the right edge of the card. */
            left: xOf(hover) + (hover > pts.length / 2 ? -12 : 12),
            transform: hover > pts.length / 2 ? 'translateX(-100%)' : 'none',
            top: 4,
            pointerEvents: 'none',
            background: 'var(--panel)',
            border: '1px solid var(--line)',
            borderRadius: 8,
            padding: '6px 9px',
            boxShadow: '0 8px 22px -10px rgba(10,10,12,.4)',
            fontSize: 11.5,
            whiteSpace: 'nowrap',
            zIndex: 2,
          }}
        >
          <div style={{ color: 'var(--ink3)' }}>{active.date}</div>
          <b style={{ fontSize: 13 }}>{format(active.value)}</b>
          <span style={{ color: 'var(--ink3)' }}> {label}</span>
          {hasOver && (
            <div style={{ marginTop: 2 }}>
              <b style={{ fontSize: 12, color: overStroke }}>
                {over[hover] == null ? 'not held' : overFormat(over[hover])}
              </b>
              <span style={{ color: 'var(--ink3)' }}> {overlay.label || 'Overlay'}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
